import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Bot } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { COMPANY } from "../lib/constants";
import api from "../lib/api";

const WhatsAppIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.5 14.4c-.3-.1-1.7-.8-2-.9-.3-.1-.5-.2-.7.2-.2.3-.8.9-.9 1.1-.2.2-.4.2-.6.1-.7-.3-1.7-.7-3-1.8-1-1-1.7-2.1-1.9-2.4-.2-.3 0-.5.1-.6l.4-.5c.1-.2.2-.3.3-.5 0-.2 0-.4 0-.5-.1-.2-.7-1.7-.9-2.3-.3-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4s-1 .9-1 2.3c0 1.4 1 2.7 1.2 2.9.1.2 2 3.1 4.9 4.4 1.7.7 2.4.8 3.3.7.5-.1 1.7-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.2-.3-.3-.6-.4zM12 2C6.5 2 2 6.5 2 12c0 1.8.5 3.5 1.3 4.9L2 22l5.3-1.4c1.4.7 3 1.1 4.7 1.1 5.5 0 10-4.5 10-10S17.5 2 12 2z"/>
  </svg>
);

export default function FloatingButtons() {
  const [chatOpen, setChatOpen] = useState(false);
  const [sessionId] = useState(() => `s_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi 👋 I'm Flare from Codeflare Labs. I can schedule a discovery meeting for you. What would you like to discuss?" },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, chatOpen]);

  const sendMessage = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setSending(true);
    try {
      const res = await api.post("/chatbot/message", { session_id: sessionId, message: text });
      const reply = res.data.reply || "...";
      setMessages((m) => [...m, { role: "assistant", text: reply, scheduled: res.data.scheduled }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry, I couldn't reach the server. Please try again or use WhatsApp." }]);
    } finally {
      setSending(false);
    }
  };

  const waUrl = `https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent(
    "Hi Codeflare Labs, I'd like to enquire about your services."
  )}`;

  return (
    <>
      {/* WhatsApp */}
      <a
        href={waUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="floating-whatsapp-btn"
        className="fixed bottom-6 right-6 z-40 flex items-center justify-center w-14 h-14 rounded-full bg-[#25D366] text-white shadow-lg hover:scale-110 transition-transform pulse-green"
        aria-label="Chat on WhatsApp"
      >
        <WhatsAppIcon size={28} />
      </a>

      {/* Chatbot trigger */}
      <button
        data-testid="floating-chatbot-btn"
        onClick={() => setChatOpen((v) => !v)}
        className="fixed bottom-24 right-6 z-40 flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-[#FF6B1A] to-[#FFAA33] text-black shadow-[0_0_24px_rgba(255,107,26,0.5)] hover:scale-110 transition-transform"
        aria-label="Open AI chatbot"
      >
        {chatOpen ? <X size={24} /> : <MessageCircle size={26} />}
      </button>

      <AnimatePresence>
        {chatOpen && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            data-testid="chatbot-panel"
            className="fixed bottom-44 right-6 z-40 w-[92vw] max-w-sm h-[520px] rounded-2xl glass overflow-hidden flex flex-col shadow-2xl"
          >
            <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-black/60">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FF6B1A] to-[#FFAA33] flex items-center justify-center text-black">
                <Bot size={18} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-white">Flare AI · Meeting Scheduler</p>
                <p className="text-[11px] text-zinc-400">Powered by Claude Sonnet 4.5</p>
              </div>
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 no-scrollbar">
              {messages.map((m, i) => (
                <div key={`msg-${i}-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-gradient-to-br from-[#FF6B1A] to-[#FFAA33] text-black rounded-br-sm"
                        : "bg-white/10 text-white rounded-bl-sm border border-white/10"
                    }`}
                  >
                    {m.text}
                    {m.scheduled && (
                      <div className="mt-2 text-xs px-2 py-1 rounded bg-green-500/20 text-green-300 border border-green-500/30">
                        ✓ Meeting saved · ID {m.scheduled.id?.slice(0, 8)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="px-3.5 py-2.5 rounded-2xl rounded-bl-sm bg-white/10 border border-white/10">
                    <span className="inline-flex gap-1">
                      <span className="w-1.5 h-1.5 bg-white/60 rounded-full animate-bounce" />
                      <span className="w-1.5 h-1.5 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: "0.15s" }} />
                      <span className="w-1.5 h-1.5 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: "0.3s" }} />
                    </span>
                  </div>
                </div>
              )}
            </div>

            <form onSubmit={sendMessage} className="border-t border-white/10 p-3 flex gap-2 bg-black/60">
              <input
                data-testid="chatbot-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-[#FF6B1A]"
              />
              <button
                data-testid="chatbot-send-btn"
                type="submit"
                disabled={!input.trim() || sending}
                className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF6B1A] to-[#FFAA33] text-black flex items-center justify-center disabled:opacity-50"
              >
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
